import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ASYNC_STATUS } from "constants/StatusConstant";
import apiHandle from "services/apiHandle";

const initialState = {
  vehicles: [],
  currentVehicle: null,

  add_vehicle_status: ASYNC_STATUS.IDLE,
  add_vehicle_error: null,

  update_vehicle_status: ASYNC_STATUS.IDLE,
  update_vehicle_error: null,

  get_vehicles_status: ASYNC_STATUS.IDLE,
  get_vehicles_error: null,

  get_vehicle_by_id_status: ASYNC_STATUS.IDLE,
  get_vehicle_by_id_error: null,

  delete_vehicle_status: ASYNC_STATUS.IDLE,
  delete_vehicle_error: null,

  ai_autofill_status: ASYNC_STATUS.IDLE,
  ai_autofill_error: null,
  ai_autofill_data: null,
};

export const addVehicleAsync = createAsyncThunk(
  "/add-vehicle",
  async (body, { rejectWithValue }) => {
    try {
      const res = await apiHandle(
        "/vehicle/add-vehicle",
        "POST",
        body
      );
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  }
);

export const updateVehicleAsync = createAsyncThunk(
  "/update-vehicle",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/vehicle/update-vehicle/${id}`, "PUT", body);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  }
);

export const getUserVehiclesAsync = createAsyncThunk(
  "/get-user-vehicles",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/vehicle/get-user-vehicles", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  }
);

export const deleteUserVehicleAsync = createAsyncThunk(
  "/delete-vehicle",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/vehicle/delete-vehicle/${id}`, "DELETE");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  }
);

export const getVehicleByIdAsync = createAsyncThunk(
  "/get-vehicle-by-id",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/vehicle/getbyidvehicle/${id}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Session expired");
    }
  }
);

export const aiAutofillVehicleSpecsAsync = createAsyncThunk(
  "/ai-autofill-vehicle-specs",
  async (body, { rejectWithValue }) => {
    try {
      const res = await apiHandle("vehicle/ai-autofill-specs", "POST", body);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch specs");
    }
  }
);

const vehicleSlice = createSlice({
  name: "vehicle",
  initialState,
  reducers: {
    resetVehicleState: (state, action) => {
      const stateKey = action.payload;
      state[stateKey] = ASYNC_STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      // add or edit vehicle
      .addCase(addVehicleAsync.pending, (state) => {
        state.add_vehicle_status = ASYNC_STATUS.LOADING;
        state.add_vehicle_error = null;
      })
      .addCase(addVehicleAsync.fulfilled, (state, action) => {
        state.add_vehicle_status = ASYNC_STATUS.SUCCEEDED;
        const { message, vehicle } = action.payload;
        const index = state.vehicles.findIndex((v) => v._id === vehicle._id);

        if (index !== -1) {
          // vehicle exists -> replace it
          state.vehicles[index] = vehicle;
        } else {
          // vehicle does not exist -> add it to top
          state.vehicles = [vehicle, ...state.vehicles];
        }
        state.add_vehicle_error = null;
      })
      .addCase(addVehicleAsync.rejected, (state, action) => {
        state.add_vehicle_status = ASYNC_STATUS.ERROR;
        state.add_vehicle_error =
          action.payload?.message || action.error.message;
      })

      // update vehicle
      .addCase(updateVehicleAsync.pending, (state) => {
        state.update_vehicle_status = ASYNC_STATUS.LOADING;
        state.update_vehicle_error = null;
      })
      .addCase(updateVehicleAsync.fulfilled, (state, action) => {
        state.update_vehicle_status = ASYNC_STATUS.SUCCEEDED;
        const { vehicle } = action.payload;
        const index = state.vehicles.findIndex((v) => v._id === vehicle._id);
        if (index !== -1) {
          state.vehicles[index] = vehicle;
        }
        state.update_vehicle_error = null;
      })
      .addCase(updateVehicleAsync.rejected, (state, action) => {
        state.update_vehicle_status = ASYNC_STATUS.ERROR;
        state.update_vehicle_error =
          action.payload?.message || action.error.message;
      })

      // get vehicles
      .addCase(getUserVehiclesAsync.pending, (state) => {
        state.get_vehicles_status = ASYNC_STATUS.LOADING;
        state.get_vehicles_error = null;
      })
      .addCase(getUserVehiclesAsync.fulfilled, (state, action) => {
        state.get_vehicles_status = ASYNC_STATUS.SUCCEEDED;
        const { vehicles } = action.payload;
        state.vehicles = vehicles;
        state.get_vehicles_error = null;
      })
      .addCase(getUserVehiclesAsync.rejected, (state, action) => {
        state.get_vehicles_status = ASYNC_STATUS.ERROR;
        state.get_vehicles_error =
          action.payload?.message || action.error.message;
      })

      // delete vehicles
      .addCase(deleteUserVehicleAsync.pending, (state) => {
        state.delete_vehicle_status = ASYNC_STATUS.LOADING;
        state.delete_vehicle_error = null;
      })
      .addCase(deleteUserVehicleAsync.fulfilled, (state, action) => {
        state.delete_vehicle_status = ASYNC_STATUS.SUCCEEDED;
        const { message, deletedVehicleId } = action.payload;
        state.vehicles = state.vehicles.filter(
          (v) => v._id !== deletedVehicleId
        );
        // showToast(message, "success");
      })
      .addCase(deleteUserVehicleAsync.rejected, (state, action) => {
        state.delete_vehicle_status = ASYNC_STATUS.ERROR;
        state.delete_vehicle_error =
          action.payload?.message || action.error.message;
      })

      // get vehicle by id
      .addCase(getVehicleByIdAsync.pending, (state) => {
        state.get_vehicle_by_id_status = ASYNC_STATUS.LOADING;
        state.get_vehicle_by_id_error = null;
        state.currentVehicle = null;
      })
      .addCase(getVehicleByIdAsync.fulfilled, (state, action) => {
        state.get_vehicle_by_id_status = ASYNC_STATUS.SUCCEEDED;
        const { vehicle } = action.payload;
        state.currentVehicle = vehicle;
        state.get_vehicle_by_id_error = null;
      })
      .addCase(getVehicleByIdAsync.rejected, (state, action) => {
        state.get_vehicle_by_id_status = ASYNC_STATUS.ERROR;
        state.get_vehicle_by_id_error =
          action.payload?.message || action.error.message;
      })

      // ai autofill vehicle specs
      .addCase(aiAutofillVehicleSpecsAsync.pending, (state) => {
        state.ai_autofill_status = ASYNC_STATUS.LOADING;
        state.ai_autofill_error = null;
        state.ai_autofill_data = null;
      })
      .addCase(aiAutofillVehicleSpecsAsync.fulfilled, (state, action) => {
        state.ai_autofill_status = ASYNC_STATUS.SUCCEEDED;
        state.ai_autofill_data = action.payload;
        state.ai_autofill_error = null;
      })
      .addCase(aiAutofillVehicleSpecsAsync.rejected, (state, action) => {
        state.ai_autofill_status = ASYNC_STATUS.ERROR;
        state.ai_autofill_error =
          action.payload?.message || action.error.message;
      });
  },
});

export const { resetVehicleState } = vehicleSlice.actions;
export default vehicleSlice.reducer;
